import { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'dibs-react-intl';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import PdpSharedCustomsMessage from './PdpSharedCustomsMessage';

//components
import { FormattedPrice } from 'dibs-buyer-price';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import {
    anywhereInWorld,
    ratesVary,
    quoteMethod,
    quotePending,
    quotePrice,
    quoteRegion,
    quoteHandlingTime,
    leadTimeMessage,
    shippingMethodDescriptions,
} from '../messages/pdpShippingInfoMessages';

import { FREE_SHIPPING_AND_COST } from 'dibs-search-product-tile/exports/constants';
import { useShippingPrice } from 'dibs-search-product-tile/exports/useShippingPrice';
import { REGION_CODES } from '../helpers/shippingHelpers';
import { PdpSharedPromoCode } from '../PdpSharedPromoCode/PdpSharedPromoCode';

import styles from './PdpSharedShippingInfo.scss';

export const PdpSharedShippingInfoComponent = ({
    viewer,
    item,
    user,
    currency,
    children,
    shipmentQuote,
    shipment,
    quoteRequestStatus,
    leadTime,
    renderTooltip,
    noLineBreaks,
    showCustomsInfo,
    isClient,
}) => {
    const [tooltipHover, setTooltipHover] = useState(false);
    const { showShippingDiscounts = false } = viewer || {};

    const relevantQuote = shipmentQuote || null;
    const {
        buyerServiceMethodDisplay: shippingMethodDisplay,
        serviceMethod,
        handlingTimeDisplay,
        showQuoteAsPending,
    } = relevantQuote || {};

    const regionCode = serviceMethod?.region?.code;
    const customQuoteShippingAddress = shipment?.shippingAddress?.displayCityStateCountry;

    let regionName =
        quoteRequestStatus === 'customQuoteProvided' ||
        quoteRequestStatus === 'customQuoteRequested'
            ? customQuoteShippingAddress
            : serviceMethod?.region?.regionName;
    if (regionCode === REGION_CODES.REST_OF_WORLD) {
        regionName = anywhereInWorld;
    }

    const price = useShippingPrice({
        viewer,
        item,
        currency,
        type: FREE_SHIPPING_AND_COST,
    });

    const fullPrice =
        relevantQuote?.subTotalAmount?.convertedAmountList?.find(p => p?.currency === currency)
            ?.amount || 0;
    const shippingMethodQuote = quoteMethod(shippingMethodDisplay);

    let shippingPriceQuote;
    if (price !== 0) {
        shippingPriceQuote = quotePrice(<FormattedPrice currency={currency} value={price} />);
    }

    const isDiscountedShipping = showShippingDiscounts && (price === 0 || fullPrice > price);

    let overviewMessage;
    if (!relevantQuote || quoteRequestStatus === 'requestQuote') {
        overviewMessage = ratesVary;
    } else if (quoteRequestStatus === 'customQuoteRequested' || showQuoteAsPending) {
        overviewMessage = (
            <span data-tn="quote-requested">{quotePending(customQuoteShippingAddress)}</span>
        );
    } else {
        overviewMessage = (
            <span data-tn="prequote-overview">
                {price === 0 && showShippingDiscounts && (
                    <span className={styles.freeShippingMessage}>
                        <FormattedMessage
                            id="pdp.PdpSharedShippingInfoComponent.freeShippingNote"
                            defaultMessage="Free Shipping: "
                        />
                    </span>
                )}
                {isDiscountedShipping && fullPrice !== 0 && (
                    <span className={styles.discountedPrice}>
                        {quotePrice(<FormattedPrice currency={currency} value={fullPrice} />)}
                    </span>
                )}
                <span className={isDiscountedShipping ? styles.salePrice : ''}>
                    {shippingPriceQuote}
                </span>
                <span
                    className={styles.hoverable}
                    onMouseEnter={() => setTooltipHover(true)}
                    onMouseLeave={() => setTooltipHover(false)}
                >
                    {shippingMethodQuote}
                    {renderTooltip && (
                        <Tooltip isVisible={tooltipHover} direction="bottom">
                            {shippingMethodDescriptions[shippingMethodDisplay]}
                        </Tooltip>
                    )}
                </span>{' '}
                {quoteRegion(regionName)}
                {!noLineBreaks && <div />}
                {quoteHandlingTime(handlingTimeDisplay)}
                {leadTime !== 'available-now' && <span data-tn="lead-time">{leadTimeMessage}</span>}
                {'.'}
            </span>
        );
    }

    return (
        <span className={styles.shippingInfoContainer}>
            <PdpSharedPromoCode
                onlyShippingPromos
                excludeShippingPromos={price === 0}
                itemId={item?.serviceId}
                user={user}
                userId={user?.serviceId}
                displayInfo={null}
            />
            {overviewMessage} {children}
            {showCustomsInfo && <PdpSharedCustomsMessage item={item} isClient={isClient} />}
        </span>
    );
};

PdpSharedShippingInfoComponent.propTypes = {
    viewer: PropTypes.object,
    item: PropTypes.object,
    shipmentQuote: PropTypes.object,
    shipment: PropTypes.object,
    currency: PropTypes.string.isRequired,
    quoteRequestStatus: PropTypes.string,
    children: PropTypes.node,
    leadTime: PropTypes.string,
    renderTooltip: PropTypes.bool,
    noLineBreaks: PropTypes.bool,
    user: PropTypes.object,
    showCustomsInfo: PropTypes.bool,
    isClient: PropTypes.bool,
};

export const PdpSharedShippingInfo = createFragmentContainer(PdpSharedShippingInfoComponent, {
    viewer: graphql`
        fragment PdpSharedShippingInfo_viewer on Viewer {
            showShippingDiscounts: featureFlag(feature: "subsidizedShippingPDP")
            ...useShippingPrice_viewer
                @arguments(
                    fetchRegionalInfo: $fetchRegionalInfo
                    userZipCode: $userZipCode
                    userCountryCode: $userCountryCode
                )
        }
    `,
    item: graphql`
        fragment PdpSharedShippingInfo_item on Item {
            serviceId
            ...PdpSharedCustomsMessage_item
            ...useShippingPrice_item @arguments(buyerId: $userId, fetchShippingCostInfo: true)
        }
    `,
    shipment: graphql`
        fragment PdpSharedShippingInfo_shipment on Shipment {
            shippingAddress {
                displayCityStateCountry
            }
        }
    `,
    shipmentQuote: graphql`
        fragment PdpSharedShippingInfo_shipmentQuote on ShipmentQuote {
            handlingTimeDisplay(displayShort: true)
            subTotalAmount {
                convertedAmountList {
                    currency
                    amount
                }
            }
            buyerServiceMethodDisplay
            serviceMethod {
                region {
                    apiType
                    code
                    regionName
                }
            }
            showQuoteAsPending
        }
    `,
    user: graphql`
        fragment PdpSharedShippingInfo_user on User {
            serviceId
            ...PdpSharedPromoCode_user
        }
    `,
});
